import { useEffect, useRef, useState } from 'react';
import {
  Anchor,
  ActionIcon,
  Badge,
  Button,
  Card,
  Checkbox,
  FileButton,
  Group,
  Image,
  NumberInput,
  Select,
  Stack,
  Switch,
  Table,
  Text,
  TextInput,
  Textarea,
} from '@mantine/core';
import type { FormSubmissionAttachmentDetail, LookupItemDetail } from '@isg/shared-types';
import {
  CEVAP_SETI_KUTUPHANESI,
  DIGER_METNI_MAX_UZUNLUK,
  DIGER_SECENEK_KODU,
  MADDE_KANIT_AYARLARI,
  MADDE_NOT_MAX_UZUNLUK,
  ORTUK_BOLUM_KODU,
  VARSAYILAN_CEVAP_SETI_KODU,
  alanSecenekleri,
  bolumluAlanlar,
  cevapSetiCoz,
  digerSeciliMi,
  formSemaSchema,
  formSemaTaslakSchema,
  gorunurAlanlar,
  maddeKanitAyari,
} from '@isg/validation';
import type {
  CevapSeti,
  FormAlan,
  FormAlanTipi,
  FormBolum,
  FormSema,
  FormSemaGorunumu,
  FormSemaTaslak,
  MaddeKanitAyari,
} from '@isg/validation';
import {
  FORM_ALAN_KAYNAK_ETIKET,
  FORM_ALAN_TIPI_ETIKET,
  MADDE_KANIT_AYARI_ETIKET,
} from '../i18n/tr';
import { SatirEylemleri, SatirSilmeSebebi, SolukSatir } from '../sozlesme';
import { ANLAM } from '../theme';

/**
 * Form motorunun EKRAN parçaları (F2·S4 — Kapsam §22.5).
 *
 * TEK KAYNAK ŞEMADIR: alanlar `@isg/validation formSemaSchema`den okunur ve
 * tipine göre bileşen seçilir — ekranda elle alan listesi YOKTUR (tip köprüsü
 * kuralı, §2.16). Kayıt görüntülemede şema KAYDIN SÜRÜMÜNDEN gelir; bu yüzden
 * sonradan çıkarılan alan burada görünür, sonradan eklenen alan görünmez —
 * §22.5/3 ekranın kendisinde uygulanır.
 *
 * MOTORUN SINIRI burada da geçerli (§22.5/2): koşullu gösterim ("şu
 * cevaplanırsa şu açılsın") ve alanlar arası hesap YOKTUR — v1 dışı, açık uç.
 */

export type CevapHaritasi = Record<string, unknown>;

/**
 * K-D1 (28.09.2026 — Kapsam §19/13) — "Diğer" metinleri (alan kodu → metin).
 * Cevap haritasından AYRI durur: sunucuya ayrı gönderilir, ayrı saklanır ve
 * raporda ayrı sayılır.
 */
export type DigerMetinleri = Record<string, string>;

/** Kaydın cevap satırlarından "Diğer" metinleri haritası (düzenlemenin başlangıç değeri). */
export function digerMetinleriniOku(
  cevaplar: readonly { alanKodu: string; digerMetni: string | null }[],
): DigerMetinleri {
  return Object.fromEntries(
    cevaplar.flatMap((c) => (c.digerMetni === null ? [] : [[c.alanKodu, c.digerMetni]])),
  );
}

export function semayiCoz(sema: unknown): FormSema | null {
  const sonuc = formSemaSchema.safeParse(sema);
  return sonuc.success ? sonuc.data : null;
}

/**
 * K-D1 — "Diğer" SEÇİLİP AÇIKLAMASI YAZILMAMIŞ alanlar. Sunucu böyle bir
 * kaydı TASLAKTA da reddeder (yarım cevap); ekran kaydet düğmesini bu listeyle
 * pasifleştirir ve sebebini yanına yazar (§2.40 — basılınca hata veren düğme
 * en kötü seçenektir).
 */
export function digerEksikleri(
  sema: FormSemaGorunumu,
  cevaplar: CevapHaritasi,
  digerMetinleri: DigerMetinleri,
): FormAlan[] {
  return sema.alanlar.filter(
    (alan) =>
      !alan.gizli &&
      digerSeciliMi(alan, cevaplar[alan.kod]) &&
      (digerMetinleri[alan.kod] ?? '').trim() === '',
  );
}

/** Pasif kaydet düğmesinin sebep cümlesi — eksik alanlar ADIYLA. */
export function digerEksigiMetni(eksikler: readonly FormAlan[]): string | null {
  if (eksikler.length === 0) return null;
  return `“Diğer” seçilen alanın açıklamasını yazın: ${eksikler.map((a) => a.etiket).join(', ')}`;
}

/** Sunucuya gidecek "Diğer" metinleri: yalnız SEÇİLİ alanların dolu metni. */
export function gonderilecekDigerMetinleri(
  sema: FormSemaGorunumu,
  cevaplar: CevapHaritasi,
  digerMetinleri: DigerMetinleri,
): DigerMetinleri {
  return Object.fromEntries(
    sema.alanlar.flatMap((alan) => {
      const metin = (digerMetinleri[alan.kod] ?? '').trim();
      return digerSeciliMi(alan, cevaplar[alan.kod]) && metin !== '' ? [[alan.kod, metin]] : [];
    }),
  );
}
/** Tek alanın giriş bileşeni — tipe göre. Salt okunur modda değer gösterilir. */
function AlanGirisi({
  sema,
  alan,
  deger,
  onChange,
  lookupKalemleri,
}: {
  sema: FormSemaGorunumu;
  alan: FormAlan;
  deger: unknown;
  onChange: (deger: unknown) => void;
  lookupKalemleri: LookupItemDetail[];
}): React.JSX.Element {
  const ortak = {
    label: alan.etiket,
    description: alan.yardim ?? undefined,
    required: alan.zorunlu,
    'data-testid': `form-alan-${alan.kod}`,
  };
  switch (alan.tip) {
    case 'metin':
      return (
        <TextInput
          {...ortak}
          value={(deger as string) ?? ''}
          onChange={(event) => onChange(event.currentTarget.value || null)}
        />
      );
    case 'uzun_metin':
      return (
        <Textarea
          {...ortak}
          autosize
          minRows={2}
          value={(deger as string) ?? ''}
          onChange={(event) => onChange(event.currentTarget.value || null)}
        />
      );
    case 'sayi':
      return (
        <NumberInput
          {...ortak}
          value={(deger as number) ?? ''}
          onChange={(value) => onChange(value === '' ? null : Number(value))}
        />
      );
    case 'tarih':
      return (
        <TextInput
          {...ortak}
          type="date"
          value={(deger as string) ?? ''}
          onChange={(event) => onChange(event.currentTarget.value || null)}
        />
      );
    case 'evet_hayir':
      // ÜÇ HAL gerekir, iki değil: "Hayır" bir CEVAPTIR (checklist'te asıl
      // bulgudur), boş bırakmak ise cevaplamamaktır. Switch bu ikisini
      // ayıramaz — zorunlu bir soruya dokunulmamış switch sessizce "Hayır"
      // sayılırdı. Bu yüzden temizlenebilir seçim kutusu kullanılır.
      return (
        <Select
          {...ortak}
          data={[
            { value: 'evet', label: 'Evet' },
            { value: 'hayir', label: 'Hayır' },
          ]}
          value={deger === true ? 'evet' : deger === false ? 'hayir' : null}
          onChange={(value) => onChange(value === null ? null : value === 'evet')}
          clearable
        />
      );
    case 'tekli_secim':
      return (
        <Select
          {...ortak}
          data={secenekVerisi(alan, lookupKalemleri)}
          value={(deger as string) ?? null}
          onChange={(value) => onChange(value)}
          clearable
        />
      );
    case 'coklu_secim':
      return (
        <Checkbox.Group
          {...ortak}
          value={(deger as string[]) ?? []}
          onChange={(value) => onChange(value.length > 0 ? value : null)}
        >
          <Stack gap="xs" mt="xs">
            {secenekVerisi(alan, lookupKalemleri).map((secenek) => (
              <Checkbox key={secenek.value} value={secenek.value} label={secenek.label} />
            ))}
          </Stack>
        </Checkbox.Group>
      );
    case 'kontrol':
      // ⚠️ Ş8 — CEVAP SEÇENEKLERİ YAN YANA DOKUNULABİLİR DÜĞMEDİR (açılır
      // liste DEĞİL): saha eldivenle, tek elle, ekrana bakmadan ilerler.
      // Seçili düğmeye yeniden basmak seçimi TEMİZLER — "cevaplamadım" ile
      // "olumsuz" ayrı şeylerdir ve düğme dizisi de bu ikisini ayırmalıdır
      // (`evet_hayir` seçicisinin `clearable`ıyla aynı gerekçe).
      return (
        <Stack gap={4} data-testid={`form-alan-${alan.kod}`}>
          <Group gap={4} wrap="nowrap" align="baseline">
            <Text size="sm" fw={500}>
              {alan.etiket}
            </Text>
            {alan.zorunlu && (
              <Text size="sm" c={ANLAM.engel} aria-hidden>
                *
              </Text>
            )}
          </Group>
          {alan.yardim && (
            <Text size="xs" c="dimmed">
              {alan.yardim}
            </Text>
          )}
          <Group gap="xs">
            {cevapSetiCoz(sema, alan).secenekler.map((secenek) => {
              const secili = deger === secenek.kod;
              return (
                <Button
                  key={secenek.kod}
                  size="sm"
                  variant={secili ? 'filled' : 'default'}
                  color={secili ? kontrolRengi(secenek.anlam) : undefined}
                  aria-pressed={secili}
                  data-testid={`form-kontrol-${alan.kod}-${secenek.kod}`}
                  onClick={() => onChange(secili ? null : secenek.kod)}
                >
                  {secenek.etiket}
                </Button>
              );
            })}
          </Group>
        </Stack>
      );
    case 'dosya':
      // Dosya alanının değeri cevapta durmaz — ek satırları taşır; yükleme
      // ancak kayıt TASLAK olarak açıldıktan sonra mümkündür (§22.5/3).
      return (
        <Text size="sm" c="dimmed" data-testid={`form-alan-${alan.kod}`}>
          {alan.etiket}: kayıt taslak olarak açıldıktan sonra ek yüklenir
          {alan.zorunlu ? ' (zorunlu)' : ''}
        </Text>
      );
  }
}

/**
 * Seçenek listesi: gömülü sabit liste ya da tenant SÖZLÜĞÜ (lookup nüshaları).
 * PTW/İBYS besleyen alanlar zorunlu olarak lookup'tan gelir (§22.5/4) — o
 * alanların değeri kalem UUID'sidir, serbest metin değil.
 */
function secenekVerisi(
  alan: FormAlan,
  lookupKalemleri: LookupItemDetail[],
): { value: string; label: string }[] {
  if (alan.lookupListeKodu) {
    return lookupKalemleri
      .filter((kalem) => kalem.listeKodu === alan.lookupListeKodu && !kalem.gizli)
      .map((kalem) => ({ value: kalem.id, label: kalem.ad }));
  }
  // K-D1 — TEK OKUYUCU: motorun eklediği "Diğer" seçeneği buradan gelir.
  return alanSecenekleri(alan).map((secenek) => ({ value: secenek.kod, label: secenek.etiket }));
}

/**
 * G1 — DOLDURMA YÜZEYİNİN KANIT AYAĞI. Ayrı bir arayüz çünkü **her çağıran
 * sunamaz:** izin talebinin içindeki tür formu (S8) kayıt satırı doğmadan
 * dolar ve orada yüklenecek bir kayıt YOKTUR. Verilmezse madde notu/fotoğrafı
 * hiç çizilmez — *sunulamayan kontrol GÖRÜNMEZ* (§2.40'ın ikinci seçeneği).
 */
export interface MaddeKanitYuzeyi {
  ekler: FormSubmissionAttachmentDetail[];
  notlar: Record<string, string>;
  onNot: (alanKodu: string, metin: string) => void;
  onFotograf: (alanKodu: string, dosya: File) => void;
  onFotografSil: (attachmentId: string) => void;
  /** Ek indirme (önizleme için) — baytlar API'den gelir, public URL YOK. */
  onOnizleme: (attachmentId: string) => Promise<Blob>;
  /** Kayıt henüz açılmadıysa yükleme yapılamaz; sebebi satırda yazılır. */
  kayitAcik: boolean;
}

/**
 * Doldurma alanlarının tamamı — gizli alanlar formdan düşer.
 *
 * ⚠️ **Ş1 — DOLDURMA EKRANI DÜZENLEYİCİNİN AYNASIDIR:** bölümler aynı sırayla,
 * aynı başlıklarla çizilir. **Başlıksız (örtük) bölümde başlık ÇİZİLMEZ** —
 * bölüm kullanmayan şablonda kullanıcı bölüm kavramını hiç görmez.
 *
 * ⚠️ **Ş8 — HER MADDEDE NOT/FOTOĞRAF** (G1): alan yalnız şablonda AÇIKSA
 * çizilir; *"zorunlu"* olan yıldızla işaretlenir ve gönderim kapısı onu
 * sunucuda dener (ikinci bir zorunluluk kuralı ekranda yazılmaz).
 */
export function FormAlanlari({
  sema,
  cevaplar,
  onChange,
  lookupKalemleri,
  kanit,
  digerMetinleri,
}: {
  sema: FormSema;
  cevaplar: CevapHaritasi;
  /**
   * ⚠️ **TEK GERİ ÇAĞRI, İKİ DEĞER:** cevaplar ve "Diğer" metinleri BİRLİKTE
   * gelir. İki ayrı geri çağrı olsaydı, seçim kalkınca (cevap + metin aynı
   * olayda değişir) ikinci çağrı birincinin henüz işlenmemiş durumunu
   * okuyup EZERDİ — kullanıcının son seçimi sessizce kaybolurdu.
   */
  onChange: (cevaplar: CevapHaritasi, digerMetinleri: DigerMetinleri) => void;
  lookupKalemleri: LookupItemDetail[];
  kanit?: MaddeKanitYuzeyi;
  /**
   * K-D1 — "Diğer" metinleri. ⚠️ **ZORUNLU PROP:** isteğe bağlı olsaydı metni
   * taşımayan bir çağıran "Diğer"i seçtirir, açıklamasını yazdıramaz ve
   * sunucu her kaydı reddederdi (§2.40 — görünen kontrol çalışır).
   */
  digerMetinleri: DigerMetinleri;
}): React.JSX.Element {
  return (
    <Stack gap="lg">
      {bolumluAlanlar(sema)
        .filter((grup) => grup.alanlar.length > 0)
        .map((grup) => (
          <Stack key={grup.bolum.kod} gap="sm" data-testid={`form-bolum-${grup.bolum.kod}`}>
            {grup.bolum.baslik !== null && (
              <Text fw={600} size="sm">
                {grup.bolum.baslik}
              </Text>
            )}
            {grup.alanlar.map((alan) => (
              <Stack key={alan.kod} gap={6}>
                <AlanGirisi
                  sema={sema}
                  alan={alan}
                  deger={cevaplar[alan.kod]}
                  lookupKalemleri={lookupKalemleri}
                  onChange={(deger) =>
                    onChange(
                      { ...cevaplar, [alan.kod]: deger },
                      // "Diğer" seçimi kalkınca metni de kalkar: seçilmeden
                      // gönderilen metni sunucu reddeder (sessizce yutmaz).
                      digerSeciliMi(alan, deger)
                        ? digerMetinleri
                        : Object.fromEntries(
                            Object.entries(digerMetinleri).filter(([kod]) => kod !== alan.kod),
                          ),
                    )
                  }
                />
                {/* K-D1 — "Diğer" seçiliyse açıklaması ZORUNLU; kutu seçimin
                    hemen altında açılır (koşullu gösterim değil: alan tipinin
                    kendi parçası — §22.5/2 sınırı aynen). */}
                {digerSeciliMi(alan, cevaplar[alan.kod]) && (
                  <TextInput
                    size="sm"
                    pl="md"
                    label={`Diğer — ${alan.etiket.toLocaleLowerCase('tr')}`}
                    placeholder="Açıklayın"
                    withAsterisk
                    maxLength={DIGER_METNI_MAX_UZUNLUK}
                    // ✏️ 28.09.2026 (ürün sahibi teyidi): SEBEP ALANIN
                    // YANINDADIR. Kaydet düğmesinin yanındaki cümle alanı
                    // adıyla söyler; burada kutunun KENDİSİ eksik olduğunu
                    // gösterir — kullanıcı sebebi aradığı yerde bulur (§2.40).
                    error={
                      (digerMetinleri[alan.kod] ?? '').trim() === '' ? 'Açıklama yazılmalı' : null
                    }
                    errorProps={{ 'data-testid': `form-diger-hata-${alan.kod}` }}
                    value={digerMetinleri[alan.kod] ?? ''}
                    onChange={(event) =>
                      onChange(cevaplar, {
                        ...digerMetinleri,
                        [alan.kod]: event.currentTarget.value,
                      })
                    }
                    data-testid={`form-diger-${alan.kod}`}
                  />
                )}
                {/* Saha turu / S5 — atıf madde yanında (kâğıtta kendi sütununda). */}
                {alan.atif && (
                  <Text size="xs" c="dimmed" data-testid={`form-atif-${alan.kod}`}>
                    Atıf: {alan.atif}
                  </Text>
                )}
                {kanit && <MaddeKaniti alan={alan} kanit={kanit} />}
              </Stack>
            ))}
          </Stack>
        ))}
    </Stack>
  );
}

/**
 * G1 — bir maddenin NOT ve FOTOĞRAF ayağı. ⚠️ **KAPALI AYAR HİÇ ÇİZİLMEZ:**
 * *"nasılsa boş kalır"* diye her maddeye not kutusu koymak, kırk maddelik bir
 * checklist'i kırk kutuyla iki katına çıkarırdı.
 */
function MaddeKaniti({
  alan,
  kanit,
}: {
  alan: FormAlan;
  kanit: MaddeKanitYuzeyi;
}): React.JSX.Element | null {
  const notAyari = maddeKanitAyari(alan, 'not');
  const fotografAyari = maddeKanitAyari(alan, 'fotograf');
  if (notAyari === 'kapali' && fotografAyari === 'kapali') return null;
  const alanEkleri = kanit.ekler.filter((ek) => ek.alanKodu === alan.kod);
  return (
    <Stack gap={4} pl="md" data-testid={`form-kanit-${alan.kod}`}>
      {notAyari !== 'kapali' && (
        <Textarea
          size="xs"
          autosize
          minRows={1}
          maxLength={MADDE_NOT_MAX_UZUNLUK}
          label="Not"
          withAsterisk={notAyari === 'zorunlu'}
          value={kanit.notlar[alan.kod] ?? ''}
          onChange={(event) => kanit.onNot(alan.kod, event.currentTarget.value)}
          data-testid={`form-not-${alan.kod}`}
        />
      )}
      {fotografAyari !== 'kapali' && (
        <Stack gap={4}>
          <Group gap="xs" align="center">
            <Text size="xs" fw={500}>
              Fotoğraf
            </Text>
            {fotografAyari === 'zorunlu' && (
              <Text size="xs" c={ANLAM.engel} aria-hidden>
                *
              </Text>
            )}
            <FileButton
              onChange={(dosya) => dosya && kanit.onFotograf(alan.kod, dosya)}
              accept="image/jpeg,image/png,image/heic"
            >
              {(props) => (
                <Button
                  {...props}
                  size="compact-xs"
                  variant="light"
                  disabled={!kanit.kayitAcik}
                  data-testid={`form-fotograf-ekle-${alan.kod}`}
                >
                  Fotoğraf ekle
                </Button>
              )}
            </FileButton>
            {/* ⚠️ PASİF DÜĞMENİN SEBEBİ YANINDA DURUR (G5 sınıfı): "neden
                basamıyorum" sorusunu kullanıcı kendi hatası sanmamalı. */}
            {!kanit.kayitAcik && (
              <Text size="xs" c="dimmed">
                Önce işyerini seçin
              </Text>
            )}
          </Group>
          {alanEkleri.length > 0 && (
            <Group gap="xs">
              {alanEkleri.map((ek) => (
                <EkOnizleme
                  key={ek.id}
                  ek={ek}
                  onOnizleme={kanit.onOnizleme}
                  // P13-c (madde 227) — silme kapısı ekin kendisinde (sunucudan).
                  onSil={ek.silmeKapisi.yazabilir ? () => kanit.onFotografSil(ek.id) : undefined}
                />
              ))}
            </Group>
          )}
        </Stack>
      )}
    </Stack>
  );
}

/**
 * KÜÇÜK GÖRSEL — baytlar API'den gelir (public URL/SAS YOK, §22.4/6), geçici
 * object URL yalnız bu bileşenin ömrü boyunca yaşar ve unmount'ta iade edilir
 * (bırakılan URL'ler sekme kapanana kadar bellekte kalırdı).
 */
function EkOnizleme({
  ek,
  onOnizleme,
  onSil,
}: {
  ek: FormSubmissionAttachmentDetail;
  onOnizleme: (attachmentId: string) => Promise<Blob>;
  onSil?: () => void;
}): React.JSX.Element {
  const [url, setUrl] = useState<string | null>(null);
  // ⚠️ **İNDİRİCİ REF'TE TUTULUR, BAĞIMLILIĞA KONMAZ.** Çağıran onu her
  // render'da yeniden kurar (satır içi ok fonksiyonu); bağımlılığa konsaydı
  // effect **her render'da yeniden koşar**, `setUrl` yeni bir render
  // doğurur ve **sonsuz indirme döngüsü** olurdu. Tek gerçek bağımlılık
  // ekin KİMLİĞİDİR.
  const indir = useRef(onOnizleme);
  indir.current = onOnizleme;
  useEffect(() => {
    let iptal = false;
    let acilan: string | null = null;
    void indir.current(ek.id).then((blob) => {
      if (iptal) return;
      acilan = URL.createObjectURL(blob);
      setUrl(acilan);
    });
    return () => {
      iptal = true;
      // Geri verilmeyen object URL'ler sekme kapanana kadar bellekte kalır.
      if (acilan) URL.revokeObjectURL(acilan);
    };
  }, [ek.id]);
  return (
    <Stack gap={2} data-testid={`form-fotograf-${ek.id}`}>
      {url === null ? (
        <Text size="xs" c="dimmed">
          {ek.dosyaAdi}
        </Text>
      ) : (
        <Image src={url} alt={ek.dosyaAdi} w={96} h={96} fit="cover" radius="sm" />
      )}
      {onSil && (
        <Button
          size="compact-xs"
          variant="subtle"
          color={ANLAM.engel}
          onClick={onSil}
          data-testid={`form-fotograf-sil-${ek.id}`}
        >
          Kaldır
        </Button>
      )}
    </Stack>
  );
}

/** Kontrol cevabının anlamı → semantik renk (G7 seti; ekran ANLAM yazar). */
function kontrolRengi(anlam: 'olumlu' | 'olumsuz' | 'kapsam_disi'): string {
  if (anlam === 'olumlu') return ANLAM.olumlu;
  if (anlam === 'olumsuz') return ANLAM.engel;
  return ANLAM.bilgi;
}

/**
 * Dolu kaydın OKUMA görünümü — KAYDIN SÜRÜMÜNÜN şemasıyla (§22.5/3).
 * Cevabı olmayan alan "—" ile görünür; şemada olmayan cevap (teoride
 * imkânsız, doğrulama engeller) sessizce yutulmaz, ayrı satırda gösterilir.
 */
export function KayitGorunumu({
  sema,
  cevaplar,
  lookupKalemleri,
  ekler = [],
  onOnizleme,
}: {
  sema: FormSema;
  cevaplar: {
    alanKodu: string;
    deger: unknown;
    notMetni: string | null;
    digerMetni: string | null;
  }[];
  lookupKalemleri: LookupItemDetail[];
  /** G1 — maddenin kanıt fotoğrafları; verilmezse görsel çizilmez. */
  ekler?: FormSubmissionAttachmentDetail[];
  onOnizleme?: (attachmentId: string) => Promise<Blob>;
}): React.JSX.Element {
  const harita = new Map(cevaplar.map((c) => [c.alanKodu, c.deger]));
  const notHaritasi = new Map(
    cevaplar.filter((c) => c.notMetni !== null).map((c) => [c.alanKodu, c.notMetni!]),
  );
  const digerHaritasi = digerMetinleriniOku(cevaplar);
  return (
    <Table data-testid="form-kayit-gorunumu" withTableBorder>
      <Table.Tbody>
        {bolumluAlanlar(sema)
          .filter((grup) => grup.alanlar.length > 0)
          .flatMap((grup) => [
            // Başlıksız (örtük) bölümde başlık satırı hiç basılmaz — Ş1.
            ...(grup.bolum.baslik !== null
              ? [
                  <Table.Tr
                    key={`bolum-${grup.bolum.kod}`}
                    data-testid={`form-gorunum-bolum-${grup.bolum.kod}`}
                  >
                    <Table.Th colSpan={2}>{grup.bolum.baslik}</Table.Th>
                  </Table.Tr>,
                ]
              : []),
            ...grup.alanlar.map((alan) => {
              const alanEkleri = ekler.filter((ek) => ek.alanKodu === alan.kod);
              const not = notHaritasi.get(alan.kod) ?? null;
              return (
                <Table.Tr key={alan.kod} data-testid={`form-cevap-${alan.kod}`}>
                  <Table.Td w="40%">
                    <Text size="sm" fw={500}>
                      {alan.etiket}
                    </Text>
                    {alan.atif && (
                      <Text size="xs" c="dimmed" data-testid={`form-gorunum-atif-${alan.kod}`}>
                        Atıf: {alan.atif}
                      </Text>
                    )}
                  </Table.Td>
                  <Table.Td>
                    <Stack gap={4}>
                      <Text size="sm">
                        {degerMetni(
                          sema,
                          alan,
                          harita.get(alan.kod),
                          lookupKalemleri,
                          digerHaritasi[alan.kod] ?? null,
                        )}
                      </Text>
                      {/* G1 — NOT VE GÖRSEL CEVABIN ALTINDA DURUR: kaydı
                          okuyan *"neden uygunsuz"* sorusunu ayrı bir sekmeye
                          gitmeden görür (P5 — sayfanın kendisi çıktıdır). */}
                      {not !== null && (
                        <Text size="xs" c="dimmed" data-testid={`form-not-gosterim-${alan.kod}`}>
                          Not: {not}
                        </Text>
                      )}
                      {onOnizleme && alanEkleri.length > 0 && (
                        <Group gap="xs">
                          {alanEkleri.map((ek) => (
                            <EkOnizleme key={ek.id} ek={ek} onOnizleme={onOnizleme} />
                          ))}
                        </Group>
                      )}
                    </Stack>
                  </Table.Td>
                </Table.Tr>
              );
            }),
          ])}
      </Table.Tbody>
    </Table>
  );
}

function degerMetni(
  sema: FormSemaGorunumu,
  alan: FormAlan,
  deger: unknown,
  lookupKalemleri: LookupItemDetail[],
  digerMetni: string | null,
): string {
  if (deger === undefined || deger === null) return '—';
  // K-D1 — "Diğer"in metni etiketin YANINDA gösterilir (çıktıyla aynı biçim).
  const etiketle = (kod: unknown, etiket: string): string =>
    kod === DIGER_SECENEK_KODU && digerSeciliMi(alan, deger) && digerMetni
      ? `${etiket}: ${digerMetni}`
      : etiket;
  // ⚠️ Ş6 — İŞARET METNE ÇEVRİLMEZ: ✓ ekranda da, kâğıtta da ✓'dır.
  if (alan.tip === 'kontrol') {
    return (
      cevapSetiCoz(sema, alan).secenekler.find((s) => s.kod === deger)?.etiket ?? String(deger)
    );
  }
  if (alan.tip === 'evet_hayir') return deger === true ? 'Evet' : 'Hayır';
  const secenekler = secenekVerisi(alan, lookupKalemleri);
  if (alan.tip === 'tekli_secim') {
    return etiketle(deger, secenekler.find((s) => s.value === deger)?.label ?? String(deger));
  }
  if (alan.tip === 'coklu_secim') {
    return (deger as string[])
      .map((kod) => etiketle(kod, secenekler.find((s) => s.value === kod)?.label ?? kod))
      .join(', ');
  }
  return String(deger);
}

/** Taslak kaydın dosya alanları: yükle / indir / sil (S3 sınırları aynen). */
export function EkBolumu({
  sema,
  ekler,
  duzenlenebilir,
  onYukle,
  onSil,
  onIndir,
}: {
  sema: FormSema;
  ekler: FormSubmissionAttachmentDetail[];
  duzenlenebilir: boolean;
  onYukle: (alanKodu: string, dosya: File) => void;
  onSil: (attachmentId: string) => void;
  onIndir: (ek: FormSubmissionAttachmentDetail) => void;
}): React.JSX.Element | null {
  const dosyaAlanlari = gorunurAlanlar(sema).filter((alan) => alan.tip === 'dosya');
  if (dosyaAlanlari.length === 0) return null;
  return (
    <Stack gap="xs" data-testid="form-ek-bolumu">
      {dosyaAlanlari.map((alan) => {
        const alanEkleri = ekler.filter((ek) => ek.alanKodu === alan.kod);
        return (
          <Stack key={alan.kod} gap={4}>
            <Group justify="space-between">
              <Text size="sm" fw={500}>
                {alan.etiket} {alan.zorunlu && <Badge size="xs">zorunlu</Badge>}
              </Text>
              {duzenlenebilir && (
                <FileButton
                  onChange={(dosya) => dosya && onYukle(alan.kod, dosya)}
                  accept="image/jpeg,image/png,image/heic,application/pdf"
                >
                  {(props) => (
                    <Button
                      {...props}
                      size="xs"
                      variant="light"
                      data-testid={`form-ek-yukle-${alan.kod}`}
                    >
                      Dosya ekle
                    </Button>
                  )}
                </FileButton>
              )}
            </Group>
            {alanEkleri.length === 0 ? (
              <Text size="xs" c="dimmed">
                Ek yok
              </Text>
            ) : (
              alanEkleri.map((ek) => (
                <Group key={ek.id} gap="xs" data-testid={`form-ek-${ek.id}`}>
                  <Text size="sm">{ek.dosyaAdi}</Text>
                  <Button size="compact-xs" variant="subtle" onClick={() => onIndir(ek)}>
                    İndir
                  </Button>
                  {/* P13-c (madde 227) — silme kapısı SATIR BAŞINA sunucudan
                      (doldurma şapkası → gönderilmiş kayıt → sahiplik). */}
                  {ek.silmeKapisi.yazabilir && (
                    <ActionIcon
                      size="sm"
                      variant="subtle"
                      color={ANLAM.engel}
                      aria-label="Eki sil"
                      data-testid={`form-ek-sil-${ek.id}`}
                      onClick={() => onSil(ek.id)}
                    >
                      ×
                    </ActionIcon>
                  )}
                </Group>
              ))
            )}
          </Stack>
        );
      })}
      <SatirSilmeSebebi
        kapilar={ekler.map((ek) => ek.silmeKapisi)}
        nesne="Eki"
        fiil="siler"
        testId="form-ek-silme-sebebi"
      />
    </Stack>
  );
}

/**
 * ŞABLON DÜZENLEYİCİ (§22.5/1 · 42.2): müşteri alan **EKLER · GİZLER ·
 * SIRALAR · ZORUNLULUĞUNU değiştirir**. Ürün alanının kimliği
 * (kod/etiket/tip/besleme) ve SİLİNMESİ ekranda da kapalıdır — asıl kapı
 * sunucuda (422), buradaki kısıtlama yalnız kullanıcıyı boşuna denemekten
 * korur.
 *
 * ⚠️ **ALAN BİR ALT KALEMDİR → SATIR İÇİ GİRİLİR (P1).** "Alan ekle" düğmesi
 * YOKTUR: listenin sonunda SOLUK SATIR durur, kaydedilince üste oturur ve
 * altında yenisi doğar. Boş şablonda ekranda **yalnız soluk satır** vardır —
 * *"önce en az bir alan ekleyin"* diyip ekleyecek yer göstermemek, sistemin
 * kullanıcıdan yapamayacağı bir şeyi istemesiydi.
 *
 * ⚠️ **HER DEĞİŞİKLİK ANINDA YAZILIR** (`onSema` → PATCH). Ara "kaydet"
 * düğmesi YOK: eskiden düzenleme yerel durumda birikiyordu ve taslak
 * yokken `onChange` **sessizce düşüyordu** — kullanıcı düğmelere basıyor,
 * hiçbir şey olmuyordu. Taslak yoksa bu bileşen **salt okunur** çizilir ve
 * yolu "Düzenlemeye başla" düğmesi açar.
 *
 * ⚠️ **SEÇİM TİPLERİ (tekli/çoklu) MÜŞTERİ ALANINDA v1'DE YOK.** Gerekçe
 * motorun kendi kuralı: seçim alanı seçenek kaynağını XOR taşır (gömülü
 * liste VEYA sözlük) ve doldurma yüzeyi bugün yalnız TEK sözlük listesini
 * yükler — açılsaydı **doldurulamayan alan** üretilirdi. Ürün alanları bu
 * tipleri taşımaya devam eder; tipleri kilitli görünür.
 */
const MUSTERI_ALAN_TIPLERI: readonly FormAlanTipi[] = [
  // ⚠️ Tur 3 — `kontrol` LİSTENİN BAŞINDA ve yeni maddenin VARSAYILANI:
  // checklist maddesi bu motorun en sık kurulan alanıdır ve seçenek kaynağı
  // sorunu (madde 121 / XOR kuralı) onda YOKTUR — set mirastan gelir.
  'kontrol',
  'metin',
  'uzun_metin',
  'sayi',
  'tarih',
  'evet_hayir',
  'dosya',
];

/** Taslak şeması: BOŞ alan listesi geçerlidir (yayın kapısı ayrıdır). */
export function taslakSemayiCoz(sema: unknown): FormSemaTaslak | null {
  const sonuc = formSemaTaslakSchema.safeParse(sema);
  return sonuc.success ? sonuc.data : null;
}

/** D1 — bir bölümün soluk satırındaki YAZILMAMIŞ madde; bölüm başına birer tane. */
interface MaddeTaslagi {
  etiket: string;
  tip: FormAlanTipi;
  zorunlu: boolean;
  fotografAyari: MaddeKanitAyari;
  notAyari: MaddeKanitAyari;
}

/**
 * D3 — YENİ MADDENİN VARSAYILANI: kanıt eksenlerinin ikisi de **isteğe
 * bağlı** doğar (ürün sahibi kararı).
 *
 * ⚠️ **Bu yalnız YENİ maddenin varsayılanıdır.** Kütüphane maddeleri ve
 * bugüne kadar açılmış maddeler ETKİLENMEZ — `maddeKanitAyari` bildirilmemiş
 * ayarı `kapali` saymaya devam eder (G1). Değişen şey kaydın anlamı değil,
 * kullanıcının hangi hâlden başladığıdır: doldurma ekranında kutular görünür
 * durur, istemeyen madde bazında kapatır.
 */
const YENI_MADDE_TASLAGI: MaddeTaslagi = {
  etiket: '',
  tip: 'kontrol',
  zorunlu: false,
  fotografAyari: 'istege_bagli',
  notAyari: 'istege_bagli',
};

/**
 * ŞABLON DÜZENLEYİCİSİ — Ş7 (Tur 3): **BÖLÜMLER → MADDELER**, satır içi.
 *
 * ⚠️ **P1 — MODAL YOK, SATIR İÇİ EKLEME:** madde de bölüm de birer ALT
 * KALEMDİR; ikisi de listenin sonundaki SOLUK SATIRDAN eklenir. *"Madde ekle"*
 * düğmesi YOKTUR ve eklenmez.
 *
 * ⚠️ **SIRA BİR SAYI DEĞİL KOMŞULUKTUR** (işlevsel düzeltme turunun kuralı,
 * aynen): ↑/↓ komşuyla takas eder ve her yazmada liste 10'ar yeniden
 * numaralanır. Sürükle-bırak AYNI işlemi çağırır — ikinci bir sıralama
 * mantığı yazılmaz; tutamak yalnız fareyle çalışır, ↑/↓ klavyeyle de çalışır.
 *
 * ⚠️ **BÖLÜMSÜZ ŞABLON TEK İSİMSİZ BÖLÜMDE YAŞAR** (Ş1) — o durumda başlık
 * satırı hiç çizilmez ve kullanıcı bölüm kavramını görmez; ilk bölüm
 * eklendiğinde örtük bölüm somutlaşır ve mevcut maddeler onun altında kalır.
 */
export function SablonDuzenleyici({
  sema,
  duzenlenebilir,
  kaydediliyor = false,
  onSema,
}: {
  sema: FormSemaTaslak;
  /** Taslak sürüm YOKSA salt okunur — yayınlanmış sürüm değişmez (§22.5/3). */
  duzenlenebilir: boolean;
  kaydediliyor?: boolean;
  onSema: (sema: FormSemaTaslak) => void;
}): React.JSX.Element {
  // ⚠️ **D1 — HER BÖLÜMÜN SOLUK SATIRI KENDİ TASLAĞINI TAŞIR.** Tek bir
  // `yeniEtiket` durumu üç bölümün üç satırını birden besliyordu: kullanıcı
  // bir bölüme madde başlığı yazarken AYNI metin diğer bölümlerin soluk
  // satırlarında da beliriyordu. Veri doğruydu (kaydedince doğru bölüme
  // gidiyordu) — **ekran yalan söylüyordu** (§2.40'ın sunum kolu). Durum
  // anahtarı BÖLÜM KODUDUR; satırlar birbirini göremez.
  const [maddeTaslaklari, setMaddeTaslaklari] = useState<Record<string, MaddeTaslagi>>({});
  const [yeniBolum, setYeniBolum] = useState('');
  const [suruklenen, setSuruklenen] = useState<string | null>(null);

  const maddeTaslagi = (bolumKodu: string): MaddeTaslagi =>
    maddeTaslaklari[bolumKodu] ?? YENI_MADDE_TASLAGI;
  const maddeTaslagiYaz = (bolumKodu: string, degisiklik: Partial<MaddeTaslagi>): void =>
    setMaddeTaslaklari((onceki) => ({
      ...onceki,
      [bolumKodu]: { ...(onceki[bolumKodu] ?? YENI_MADDE_TASLAGI), ...degisiklik },
    }));

  const kilitli = !duzenlenebilir || kaydediliyor;
  const bolumler = duzenlemeBolumleri(sema);
  const ortukTekBolum = (sema.bolumler ?? []).length === 0;
  const gruplar = bolumler.map((bolum) => ({
    bolum,
    alanlar: bolumunAlanlari(sema, bolumler, bolum.kod),
  }));
  const setSecenekleri = duzenlemeSetleri(sema);

  /** Her yazma: bölüm sırası + madde sırası 10'ar YENİDEN numaralanır. */
  const yaz = (
    yeniBolumler: FormBolum[] | null,
    alanSirasi: FormAlan[],
    ek: Partial<FormSemaTaslak> = {},
  ): void => {
    onSema({
      ...sema,
      ...ek,
      ...(yeniBolumler === null
        ? {}
        : { bolumler: yeniBolumler.map((b, i) => ({ ...b, sira: (i + 1) * 10 })) }),
      alanlar: alanSirasi.map((alan, i) => ({ ...alan, sira: (i + 1) * 10 })),
    });
  };

  const duzAlanlar = (): FormAlan[] => gruplar.flatMap((g) => g.alanlar);

  const alanGuncelle = (kod: string, degisiklik: Partial<FormAlan>): void => {
    yaz(
      null,
      duzAlanlar().map((alan) => (alan.kod === kod ? { ...alan, ...degisiklik } : alan)),
    );
  };

  /** Maddeyi hedef maddenin YERİNE taşır (↑/↓ ve sürükle-bırak aynı yol). */
  const maddeyiTasi = (kod: string, hedefKod: string): void => {
    const duz = duzAlanlar();
    const kaynakDizin = duz.findIndex((a) => a.kod === kod);
    const hedefDizin = duz.findIndex((a) => a.kod === hedefKod);
    if (kaynakDizin < 0 || hedefDizin < 0 || kaynakDizin === hedefDizin) return;
    const hedefBolum = duz[hedefDizin]!.bolumKodu ?? null;
    const yeniDizi = [...duz];
    const [tasinan] = yeniDizi.splice(kaynakDizin, 1);
    yeniDizi.splice(hedefDizin, 0, { ...tasinan!, bolumKodu: hedefBolum });
    yaz(null, yeniDizi);
  };

  const bolumTasi = (kod: string, yon: -1 | 1): void => {
    const dizin = bolumler.findIndex((b) => b.kod === kod);
    const hedef = dizin + yon;
    if (dizin < 0 || hedef < 0 || hedef >= bolumler.length) return;
    const yeniBolumler = [...bolumler];
    const tasinan = yeniBolumler[dizin]!;
    yeniBolumler[dizin] = yeniBolumler[hedef]!;
    yeniBolumler[hedef] = tasinan;
    yaz(
      yeniBolumler,
      yeniBolumler.flatMap((b) => bolumunAlanlari(sema, bolumler, b.kod)),
    );
  };

  const maddeEkle = (bolumKodu: string): void => {
    const taslak = maddeTaslagi(bolumKodu);
    const etiket = taslak.etiket.trim();
    if (!etiket) return;
    const yeniAlan: FormAlan = {
      kod: benzersizAlanKodu(sema, etiket),
      etiket,
      tip: taslak.tip,
      zorunlu: taslak.zorunlu,
      gizli: false,
      sira: 0,
      kaynak: 'musteri',
      // D3 — kanıt ayarları madde AÇILIRKEN iner. ⚠️ `dosya` alanı zaten ek
      // taşır ve şema ona kanıt ekseni verilmesini 422'ler (G1); o tipte
      // ayar YAZILMAZ — ekran da o iki hücrede "—" gösterir.
      ...(taslak.tip === 'dosya'
        ? {}
        : { fotografAyari: taslak.fotografAyari, notAyari: taslak.notAyari }),
      ...(ortukTekBolum ? {} : { bolumKodu }),
    };
    const duz = duzAlanlar();
    // Madde KENDİ bölümünün sonuna girer (listenin sonuna değil).
    const sonDizin = duz.map((a) => a.bolumKodu ?? bolumler[0]!.kod).lastIndexOf(bolumKodu);
    const yeniDizi = [...duz];
    yeniDizi.splice(sonDizin + 1, 0, yeniAlan);
    yaz(null, yeniDizi);
    // YALNIZ o bölümün taslağı sıfırlanır — komşu satırlar dokunulmaz kalır.
    setMaddeTaslaklari((onceki) => ({ ...onceki, [bolumKodu]: YENI_MADDE_TASLAGI }));
  };

  const bolumEkle = (): void => {
    const baslik = yeniBolum.trim();
    if (!baslik) return;
    const mevcut = sema.bolumler ?? [];
    const taban =
      mevcut.length === 0
        ? // Örtük bölüm SOMUTLAŞIR: başlığı null kalır (çizilmez) ve mevcut
          // maddeler onun altında durur — kullanıcı bir şey kaybetmez.
          [{ kod: ORTUK_BOLUM_KODU, baslik: null, sira: 10 }]
        : [...mevcut];
    const eklenen: FormBolum = {
      kod: benzersizBolumKodu(taban, baslik),
      baslik,
      sira: (taban.length + 1) * 10,
    };
    const yeniBolumler = [...taban, eklenen];
    yaz(
      yeniBolumler,
      duzAlanlar().map((alan) => ({ ...alan, bolumKodu: alan.bolumKodu ?? ORTUK_BOLUM_KODU })),
    );
    setYeniBolum('');
  };

  /** Bölüm silmek MADDELERİ SİLMEZ — bir öncekine taşır (veri kaybı yok). */
  const bolumSil = (kod: string): void => {
    const kalan = bolumler.filter((b) => b.kod !== kod);
    if (kalan.length === 0) return;
    const hedef = kalan[0]!.kod;
    yaz(
      kalan,
      kalan.flatMap((b) =>
        bolumunAlanlari(sema, bolumler, b.kod).concat(
          b.kod === hedef
            ? bolumunAlanlari(sema, bolumler, kod).map((a) => ({ ...a, bolumKodu: hedef }))
            : [],
        ),
      ),
    );
  };

  return (
    <Stack
      gap="lg"
      data-testid="sablon-duzenleyici"
      data-duzenlenebilir={duzenlenebilir ? 'evet' : 'hayir'}
    >
      {/* ⚠️ **G3 — İKİ BLOK GÖRSEL OLARAK AYRIDIR.** İkisi de "tablo + soluk
          satır" olduğu için sınırsız dizildiklerinde tek bir liste gibi
          okunuyorlardı: ürün sahibi cevap seti soluk satırını BÖLÜM EKLEME
          satırı sandı. Ayrım kart + başlıkla yapılır — *aynı desen iki farklı
          işi anlatıyorsa, işleri ayıran şey çerçevedir.* */}
      <Card withBorder data-testid="sablon-cevap-seti-karti">
        <Stack gap="xs">
          <Text fw={600} size="sm">
            Cevap setleri
          </Text>
          <Text size="xs" c="dimmed">
            Maddelerin hangi sözcüklerle cevaplanacağını belirler.
          </Text>
          <CevapSetleriDuzenleyici
            sema={sema}
            kilitli={kilitli}
            onSema={onSema}
            setSecenekleri={setSecenekleri}
          />
        </Stack>
      </Card>

      <Card withBorder data-testid="sablon-madde-karti">
        <Stack gap="xs">
          <Text fw={600} size="sm">
            Bölümler ve maddeler
          </Text>
          <Text size="xs" c="dimmed">
            Formun kendisi: bölüm başlıkları ve altlarındaki sorular.
          </Text>
          <Table withTableBorder data-testid="sablon-madde-tablosu">
            <Table.Thead>
              <Table.Tr>
                <Table.Th w={40} />
                <Table.Th>Madde</Table.Th>
                {/* Saha turu / S5 — ATIF: maddenin dayandığı referans (ISO ·
                    mevzuat · prosedür). Serbest metin; motor yalnız taşır. */}
                <Table.Th w={160}>Atıf</Table.Th>
                <Table.Th w={150}>Tip</Table.Th>
                <Table.Th w={170}>Cevap seti</Table.Th>
                {/* G1 — MADDE BAZINDA KANIT: iki ayar, üçer hâl (Ş5). */}
                <Table.Th w={140}>Fotoğraf</Table.Th>
                <Table.Th w={140}>Not</Table.Th>
                <Table.Th w={90}>Zorunlu</Table.Th>
                <Table.Th w={90}>Gizli</Table.Th>
                <Table.Th w={90}>Sıra</Table.Th>
                <Table.Th w={70} />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {gruplar.flatMap(({ bolum, alanlar }, bolumDizin) => [
                ...(ortukTekBolum
                  ? []
                  : [
                      <Table.Tr
                        key={`bolum-${bolum.kod}`}
                        data-testid={`sablon-bolum-${bolum.kod}`}
                      >
                        <Table.Td />
                        <Table.Td colSpan={8}>
                          <BolumBasligi
                            bolum={bolum}
                            kilitli={kilitli}
                            onBaslik={(baslik) =>
                              yaz(
                                bolumler.map((b) => (b.kod === bolum.kod ? { ...b, baslik } : b)),
                                duzAlanlar(),
                              )
                            }
                          />
                        </Table.Td>
                        <Table.Td>
                          <Group gap={4} wrap="nowrap">
                            <ActionIcon
                              size="sm"
                              variant="subtle"
                              disabled={kilitli || bolumDizin === 0}
                              aria-label={`${bolum.baslik ?? 'Bölüm'} yukarı`}
                              data-testid={`sablon-bolum-yukari-${bolum.kod}`}
                              onClick={() => bolumTasi(bolum.kod, -1)}
                            >
                              ↑
                            </ActionIcon>
                            <ActionIcon
                              size="sm"
                              variant="subtle"
                              disabled={kilitli || bolumDizin === gruplar.length - 1}
                              aria-label={`${bolum.baslik ?? 'Bölüm'} aşağı`}
                              data-testid={`sablon-bolum-asagi-${bolum.kod}`}
                              onClick={() => bolumTasi(bolum.kod, 1)}
                            >
                              ↓
                            </ActionIcon>
                          </Group>
                        </Table.Td>
                        <Table.Td>
                          <SatirEylemleri
                            testId={`sablon-bolum-eylemler-${bolum.kod}`}
                            digerleri={[
                              {
                                etiket: 'Bölümü sil',
                                testId: `sablon-bolum-sil-${bolum.kod}`,
                                tehlikeli: true,
                                pasif: kilitli || gruplar.length < 2,
                                onClick: () => bolumSil(bolum.kod),
                              },
                            ]}
                          />
                        </Table.Td>
                      </Table.Tr>,
                    ]),
                ...alanlar.map((alan, dizin) => (
                  <Table.Tr
                    key={alan.kod}
                    data-testid={`sablon-alan-${alan.kod}`}
                    draggable={!kilitli}
                    onDragStart={() => setSuruklenen(alan.kod)}
                    onDragOver={(event) => {
                      if (suruklenen && suruklenen !== alan.kod) event.preventDefault();
                    }}
                    onDrop={() => {
                      if (suruklenen) maddeyiTasi(suruklenen, alan.kod);
                      setSuruklenen(null);
                    }}
                    onDragEnd={() => setSuruklenen(null)}
                  >
                    <Table.Td>
                      <Text
                        size="sm"
                        c="dimmed"
                        aria-hidden
                        data-testid={`sablon-tutamak-${alan.kod}`}
                      >
                        ⠿
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <AlanEtiketi
                        alan={alan}
                        kilitli={kilitli}
                        onEtiket={(etiket) => alanGuncelle(alan.kod, { etiket })}
                      />
                    </Table.Td>
                    <Table.Td>
                      {/* S5 — atıf ürün maddesinde de SERBEST (müşterinin kendi
                          prosedürüne çapraz referans; anlamı değil bağlamı). */}
                      <TextInput
                        size="xs"
                        disabled={kilitli}
                        placeholder="ör. 6331 md.15"
                        aria-label={`${alan.etiket} atıf`}
                        defaultValue={alan.atif ?? ''}
                        key={`atif-${alan.kod}-${alan.atif ?? ''}`}
                        onBlur={(event) => {
                          const deger = event.currentTarget.value.trim();
                          if ((alan.atif ?? '') !== deger) {
                            alanGuncelle(alan.kod, { atif: deger ? deger : null });
                          }
                        }}
                        data-testid={`sablon-atif-${alan.kod}`}
                      />
                    </Table.Td>
                    <Table.Td>
                      <AlanTipi
                        alan={alan}
                        kilitli={kilitli}
                        onTip={(tip) =>
                          alanGuncelle(alan.kod, {
                            tip,
                            // Tip kontrolden çıkarsa set bağı DÜŞER — başka tipte
                            // duran set, doldurma yüzeyinin okumayacağı ölü ayardır.
                            ...(tip === 'kontrol' ? {} : { cevapSetiKodu: null }),
                          })
                        }
                      />
                    </Table.Td>
                    <Table.Td>
                      {alan.tip === 'kontrol' ? (
                        <Select
                          size="xs"
                          disabled={kilitli}
                          aria-label={`${alan.etiket} cevap seti`}
                          data={[
                            {
                              value: '',
                              label: `Şablon varsayılanı (${setSecenekleri.varsayilanAd})`,
                            },
                            ...setSecenekleri.secenekler,
                          ]}
                          value={alan.cevapSetiKodu ?? ''}
                          allowDeselect={false}
                          onChange={(value) =>
                            alanGuncelle(alan.kod, { cevapSetiKodu: value ? value : null })
                          }
                          data-testid={`sablon-cevap-seti-${alan.kod}`}
                        />
                      ) : (
                        <Text size="xs" c="dimmed">
                          —
                        </Text>
                      )}
                    </Table.Td>
                    <Table.Td>
                      <KanitAyariSecici
                        tip={alan.tip}
                        deger={maddeKanitAyari(alan, 'fotograf')}
                        kilitli={kilitli}
                        ariaEtiketi={`${alan.etiket} fotoğraf ayarı`}
                        testId={`sablon-fotograf-ayari-${alan.kod}`}
                        onDeger={(deger) => alanGuncelle(alan.kod, { fotografAyari: deger })}
                      />
                    </Table.Td>
                    <Table.Td>
                      <KanitAyariSecici
                        tip={alan.tip}
                        deger={maddeKanitAyari(alan, 'not')}
                        kilitli={kilitli}
                        ariaEtiketi={`${alan.etiket} not ayarı`}
                        testId={`sablon-not-ayari-${alan.kod}`}
                        onDeger={(deger) => alanGuncelle(alan.kod, { notAyari: deger })}
                      />
                    </Table.Td>
                    <Table.Td>
                      <Switch
                        size="sm"
                        disabled={kilitli}
                        checked={alan.zorunlu}
                        aria-label={`${alan.etiket} zorunlu mu`}
                        onChange={(event) =>
                          alanGuncelle(alan.kod, { zorunlu: event.currentTarget.checked })
                        }
                        data-testid={`sablon-zorunlu-${alan.kod}`}
                      />
                    </Table.Td>
                    <Table.Td>
                      <Switch
                        size="sm"
                        disabled={kilitli}
                        checked={alan.gizli}
                        aria-label={`${alan.etiket} gizli mi`}
                        onChange={(event) =>
                          alanGuncelle(alan.kod, { gizli: event.currentTarget.checked })
                        }
                        data-testid={`sablon-gizli-${alan.kod}`}
                      />
                    </Table.Td>
                    <Table.Td>
                      {/* Sıra SAYI DEĞİL KOMŞULUK: her tuş bir yazma (elle girilen
                      numara çakışır ve her tuşa basışta istek üretirdi). */}
                      <Group gap={4} wrap="nowrap">
                        <ActionIcon
                          size="sm"
                          variant="subtle"
                          disabled={kilitli || dizin === 0}
                          aria-label={`${alan.etiket} yukarı`}
                          data-testid={`sablon-yukari-${alan.kod}`}
                          onClick={() => maddeyiTasi(alan.kod, alanlar[dizin - 1]!.kod)}
                        >
                          ↑
                        </ActionIcon>
                        <ActionIcon
                          size="sm"
                          variant="subtle"
                          disabled={kilitli || dizin === alanlar.length - 1}
                          aria-label={`${alan.etiket} aşağı`}
                          data-testid={`sablon-asagi-${alan.kod}`}
                          onClick={() => maddeyiTasi(alan.kod, alanlar[dizin + 1]!.kod)}
                        >
                          ↓
                        </ActionIcon>
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      {/* P2-a: SİLME geri alınamaz → satırda değil ⋮ altında.
                      ÜRÜN alanında hiç yok (silinemez, gizlenir — §22.5/1). */}
                      <SatirEylemleri
                        testId={`sablon-alan-eylemler-${alan.kod}`}
                        digerleri={[
                          {
                            etiket: 'Maddeyi sil',
                            testId: `sablon-sil-${alan.kod}`,
                            tehlikeli: true,
                            gizli: alan.kaynak !== 'musteri',
                            pasif: kilitli,
                            onClick: () =>
                              yaz(
                                null,
                                duzAlanlar().filter((a) => a.kod !== alan.kod),
                              ),
                          },
                        ]}
                      />
                    </Table.Td>
                  </Table.Tr>
                )),
                ...(duzenlenebilir
                  ? [
                      (() => {
                        const taslak = maddeTaslagi(bolum.kod);
                        // D1 — her hücrenin kimliği BÖLÜM KODUYLA ayrışır;
                        // örtük tek bölümde eski (bölümsüz) kimlikler korunur.
                        const kimlik = (ek: string): string =>
                          ortukTekBolum ? `sablon-soluk-${ek}` : `sablon-soluk-${ek}-${bolum.kod}`;
                        const bolumEki = bolum.baslik ? ` — ${bolum.baslik}` : '';
                        return (
                          <SolukSatir
                            key={`soluk-${bolum.kod}`}
                            testId={
                              ortukTekBolum
                                ? 'sablon-alan-soluk-satir'
                                : `sablon-madde-soluk-${bolum.kod}`
                            }
                            dokunuldu={taslak.etiket.trim().length > 0}
                            kaydedilebilir={taslak.etiket.trim().length > 0}
                            kaydediliyor={kaydediliyor}
                            onKaydet={() => maddeEkle(bolum.kod)}
                            kolonSayisi={10}
                          >
                            <Table.Td />
                            <Table.Td>
                              {/* ⚠️ G3 — SOLUK SATIRIN DİLİ EYLEMDİR: *"Madde
                              başlığı"* bir ALAN ADIDIR ve satırın ne işe
                              yaradığını söylemez; ürün sahibi cevap seti
                              satırını bölüm ekleme satırı sandı. *"+ Madde
                              ekle"* satırın KENDİSİNİ anlatır. */}
                              <TextInput
                                placeholder="+ Madde ekle"
                                aria-label={`Yeni madde başlığı${bolumEki}`}
                                value={taslak.etiket}
                                onChange={(event) =>
                                  maddeTaslagiYaz(bolum.kod, { etiket: event.currentTarget.value })
                                }
                                data-testid={kimlik('etiket')}
                              />
                            </Table.Td>
                            {/* Atıf maddenin KENDİ satırında girilir (kaydedilince). */}
                            <Table.Td />
                            <Table.Td>
                              <Select
                                aria-label={`Yeni madde tipi${bolumEki}`}
                                data={MUSTERI_ALAN_TIPLERI.map((tip) => ({
                                  value: tip,
                                  label: FORM_ALAN_TIPI_ETIKET[tip] ?? tip,
                                }))}
                                value={taslak.tip}
                                onChange={(value) =>
                                  maddeTaslagiYaz(bolum.kod, {
                                    tip: (value as FormAlanTipi | null) ?? 'kontrol',
                                  })
                                }
                                allowDeselect={false}
                                data-testid={kimlik('tip')}
                              />
                            </Table.Td>
                            {/* Cevap seti maddenin KENDİ satırında seçilir: seçenek
                            listesi şablonun setlerine bağlıdır ve soluk satırda
                            "hangi set" sorusu maddenin adından önce gelirdi. */}
                            <Table.Td />
                            {/* ⚠️ **D3 — KANIT AYARI MADDE YAZILIRKEN VERİLİR.**
                            Eskiden bu iki hücre boştu ve ayar ancak madde
                            kaydedildikten SONRA satırında açılıyordu: kırk
                            maddelik bir checklist kırk kez iki adımlık iş
                            demekti (P1-b'nin "art arda giriş ritmi"). */}
                            <Table.Td>
                              <KanitAyariSecici
                                tip={taslak.tip}
                                deger={taslak.fotografAyari}
                                kilitli={kaydediliyor}
                                ariaEtiketi={`Yeni madde fotoğraf ayarı${bolumEki}`}
                                testId={kimlik('fotograf')}
                                onDeger={(deger) =>
                                  maddeTaslagiYaz(bolum.kod, { fotografAyari: deger })
                                }
                              />
                            </Table.Td>
                            <Table.Td>
                              <KanitAyariSecici
                                tip={taslak.tip}
                                deger={taslak.notAyari}
                                kilitli={kaydediliyor}
                                ariaEtiketi={`Yeni madde not ayarı${bolumEki}`}
                                testId={kimlik('not')}
                                onDeger={(deger) => maddeTaslagiYaz(bolum.kod, { notAyari: deger })}
                              />
                            </Table.Td>
                            <Table.Td>
                              <Switch
                                size="sm"
                                aria-label={`Yeni madde zorunlu mu${bolumEki}`}
                                checked={taslak.zorunlu}
                                onChange={(event) =>
                                  maddeTaslagiYaz(bolum.kod, {
                                    zorunlu: event.currentTarget.checked,
                                  })
                                }
                                data-testid={kimlik('zorunlu')}
                              />
                            </Table.Td>
                            <Table.Td />
                            <Table.Td />
                          </SolukSatir>
                        );
                      })(),
                    ]
                  : []),
              ])}
              {duzenlenebilir && (
                <SolukSatir
                  testId="sablon-bolum-soluk-satir"
                  dokunuldu={yeniBolum.trim().length > 0}
                  kaydedilebilir={yeniBolum.trim().length > 0}
                  kaydediliyor={kaydediliyor}
                  onKaydet={bolumEkle}
                  kolonSayisi={9}
                >
                  <Table.Td />
                  <Table.Td colSpan={8}>
                    <TextInput
                      placeholder="+ Bölüm ekle"
                      aria-label="Yeni bölüm başlığı"
                      value={yeniBolum}
                      onChange={(event) => setYeniBolum(event.currentTarget.value)}
                      data-testid="sablon-soluk-bolum"
                    />
                  </Table.Td>
                </SolukSatir>
              )}
            </Table.Tbody>
          </Table>
          {/* ⚠️ **G4 — KULLANICI YÜZEYİNDE BELGE REFERANSI YOKTUR.** Eskiden
              bu satır *"(Kapsam §22.5)"* ile bitiyordu: kullanıcının açıp
              okuyamayacağı bir iç belgeye atıf, metni bir HATA KODU gibi
              gösterir (§2.40'ın D2 kolu — kullanıcı ne yapacağını duyar). */}
          <Text size="xs" c="dimmed">
            Ürün maddeleri silinemez, gizlenir. Maddeler arası hesap/skor ve koşullu gösterim bu
            sürümde yoktur.
          </Text>
        </Stack>
      </Card>
    </Stack>
  );
}

/**
 * G1 — MADDE BAZINDA KANIT AYARI (fotoğraf / not). Üç hâl, tek bileşen:
 * ikisi **aynı** kuralı taşır (Ş5) ve ayrı ayrı yazılsalardı biri
 * güncellenip diğeri unutulurdu.
 *
 * ⚠️ **D3 — BİLEŞEN ALANA DEĞİL DEĞERE BAĞLIDIR.** İki çağıranı vardır:
 * kaydedilmiş maddenin satırı ve **soluk satırdaki yazılmamış madde**.
 * Kaydı okumasaydı ikinci çağıran kendi seçicisini yazmak zorunda kalır,
 * `dosya` muafiyeti ile üç hâlin sırası iki yerde ayrı ayrı yaşardı.
 */
function KanitAyariSecici({
  tip,
  deger,
  kilitli,
  ariaEtiketi,
  testId,
  onDeger,
}: {
  tip: FormAlanTipi;
  deger: MaddeKanitAyari;
  kilitli: boolean;
  ariaEtiketi: string;
  testId: string;
  onDeger: (deger: MaddeKanitAyari) => void;
}): React.JSX.Element {
  // Dosya alanı zaten ek taşır — ikinci bir kanıt ekseni sunucuda 422'dir;
  // ekranda da sunulmaz (görünen kontrol çalışır — §2.40).
  if (tip === 'dosya') {
    return (
      <Text size="xs" c="dimmed">
        —
      </Text>
    );
  }
  return (
    <Select
      size="xs"
      disabled={kilitli}
      aria-label={ariaEtiketi}
      data={MADDE_KANIT_AYARLARI.map((secenek) => ({
        value: secenek,
        label: MADDE_KANIT_AYARI_ETIKET[secenek] ?? secenek,
      }))}
      value={deger}
      allowDeselect={false}
      onChange={(value) => onDeger((value as MaddeKanitAyari | null) ?? 'kapali')}
      data-testid={testId}
    />
  );
}

/**
 * Ş2 — CEVAP SETLERİ. Kütüphane seti seçilince şablonun sürümüne KOPYALANIR
 * (dağıtım INSERT-ONLY: aynı kodlu set varsa ezilmez); müşteri kendi setini
 * satır içi yazar (madde 121).
 *
 * ⚠️ **KONSOLİDASYON ŞERHİ EKRANDA DA DURUR:** serbest seçenekli maddeler
 * holding konsolidasyonunda ortak eksen taşımaz — kullanıcı bunu seti
 * yazarken görmelidir, raporu okurken değil.
 */
function CevapSetleriDuzenleyici({
  sema,
  kilitli,
  onSema,
  setSecenekleri,
}: {
  sema: FormSemaTaslak;
  kilitli: boolean;
  onSema: (sema: FormSemaTaslak) => void;
  setSecenekleri: { secenekler: { value: string; label: string }[]; varsayilanAd: string };
}): React.JSX.Element {
  const [yeniAd, setYeniAd] = useState('');
  const [olumluEtiket, setOlumluEtiket] = useState('');
  const [olumsuzEtiket, setOlumsuzEtiket] = useState('');
  const setler = sema.cevapSetleri ?? [];

  const setleriYaz = (yeniSetler: CevapSeti[], ek: Partial<FormSemaTaslak> = {}): void =>
    onSema({ ...sema, ...ek, cevapSetleri: yeniSetler });

  /** Kütüphane seti KOPYALANIR — aynı kodlu set varsa DOKUNULMAZ. */
  const kutuphaneEkle = (kod: string): void => {
    if (setler.some((s) => s.kod === kod)) return;
    const kaynak = CEVAP_SETI_KUTUPHANESI.find((s) => s.kod === kod);
    if (!kaynak) return;
    setleriYaz([...setler, { ...kaynak, secenekler: kaynak.secenekler.map((o) => ({ ...o })) }]);
  };

  const musteriSetiEkle = (): void => {
    const ad = yeniAd.trim();
    const olumlu = olumluEtiket.trim();
    const olumsuz = olumsuzEtiket.trim();
    if (!ad || !olumlu || !olumsuz) return;
    setleriYaz([
      ...setler,
      {
        kod: benzersizSetKodu(setler, ad),
        ad,
        kaynak: 'musteri',
        kapsamDisi: false,
        secenekler: [
          { kod: 'olumlu', etiket: olumlu, anlam: 'olumlu' },
          { kod: 'olumsuz', etiket: olumsuz, anlam: 'olumsuz' },
        ],
      },
    ]);
    setYeniAd('');
    setOlumluEtiket('');
    setOlumsuzEtiket('');
  };

  return (
    <Stack gap="xs" data-testid="sablon-cevap-setleri">
      <Group justify="space-between" align="flex-end">
        <Select
          size="xs"
          w={280}
          label="Şablonun varsayılan cevap seti"
          description="Maddeler bunu miras alır; satırda değiştirilebilir"
          disabled={kilitli}
          data={setSecenekleri.secenekler}
          value={sema.varsayilanCevapSetiKodu ?? VARSAYILAN_CEVAP_SETI_KODU}
          allowDeselect={false}
          onChange={(value) => {
            if (!value) return;
            // Kütüphane setine geçiliyorsa önce sürüme KOPYALANIR — set içeriği
            // sürümle dondurulur (Ş2), sonradan kütüphane değişse bile.
            const kutuphane = CEVAP_SETI_KUTUPHANESI.find((s) => s.kod === value);
            const setlerYeni =
              kutuphane && !setler.some((s) => s.kod === value)
                ? [
                    ...setler,
                    { ...kutuphane, secenekler: kutuphane.secenekler.map((o) => ({ ...o })) },
                  ]
                : setler;
            setleriYaz(setlerYeni, { varsayilanCevapSetiKodu: value });
          }}
          data-testid="sablon-varsayilan-cevap-seti"
        />
        <Select
          size="xs"
          w={260}
          label="Kütüphaneden set ekle"
          placeholder="Set seçin"
          disabled={kilitli}
          data={CEVAP_SETI_KUTUPHANESI.filter((s) => !setler.some((m) => m.kod === s.kod)).map(
            (s) => ({ value: s.kod, label: s.ad }),
          )}
          value={null}
          onChange={(value) => value && kutuphaneEkle(value)}
          data-testid="sablon-kutuphane-set-ekle"
        />
      </Group>
      <Table withTableBorder data-testid="sablon-cevap-seti-tablosu">
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Cevap seti</Table.Th>
            <Table.Th>Seçenekler</Table.Th>
            <Table.Th w={140}>Kapsam dışı</Table.Th>
            <Table.Th w={70} />
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {setler.map((set) => (
            <Table.Tr key={set.kod} data-testid={`sablon-set-${set.kod}`}>
              <Table.Td>
                <Text size="sm">{set.ad}</Text>
              </Table.Td>
              <Table.Td>
                <Text size="sm">{set.secenekler.map((o) => o.etiket).join(' · ')}</Text>
              </Table.Td>
              <Table.Td>
                {/* Ş4 — üçüncü seçenek İSTEĞE BAĞLIDIR ve şablonu kuran açar.
                    Açıkken işaretlenen madde UYGUNSUZ SAYILMAZ ve tamamlanma
                    sayımında EKSİK GÖRÜNMEZ. */}
                <Switch
                  size="sm"
                  disabled={kilitli}
                  checked={set.kapsamDisi}
                  aria-label={`${set.ad} kapsam dışı seçeneği`}
                  data-testid={`sablon-set-kapsam-disi-${set.kod}`}
                  onChange={(event) =>
                    setleriYaz(
                      setler.map((s) =>
                        s.kod === set.kod ? { ...s, kapsamDisi: event.currentTarget.checked } : s,
                      ),
                    )
                  }
                />
              </Table.Td>
              <Table.Td>
                <SatirEylemleri
                  testId={`sablon-set-eylemler-${set.kod}`}
                  digerleri={[
                    {
                      etiket: 'Seti kaldır',
                      testId: `sablon-set-sil-${set.kod}`,
                      tehlikeli: true,
                      // Kullanılan set kaldırılamaz: maddeleri seçeneksiz
                      // bırakırdı (sunucu da 422 verirdi — ekran boşuna
                      // denemeyi önler, kararı sunucu verir).
                      pasif:
                        kilitli ||
                        sema.varsayilanCevapSetiKodu === set.kod ||
                        sema.alanlar.some((a) => a.cevapSetiKodu === set.kod),
                      onClick: () => setleriYaz(setler.filter((s) => s.kod !== set.kod)),
                    },
                  ]}
                />
              </Table.Td>
            </Table.Tr>
          ))}
          {!kilitli && (
            <SolukSatir
              testId="sablon-set-soluk-satir"
              dokunuldu={yeniAd.trim().length > 0}
              kaydedilebilir={
                yeniAd.trim().length > 0 &&
                olumluEtiket.trim().length > 0 &&
                olumsuzEtiket.trim().length > 0
              }
              onKaydet={musteriSetiEkle}
              kolonSayisi={4}
            >
              <Table.Td>
                <TextInput
                  placeholder="Set adı"
                  aria-label="Yeni cevap seti adı"
                  value={yeniAd}
                  onChange={(event) => setYeniAd(event.currentTarget.value)}
                  data-testid="sablon-set-soluk-ad"
                />
              </Table.Td>
              <Table.Td>
                <Group gap="xs" wrap="nowrap">
                  <TextInput
                    placeholder="Olumlu"
                    aria-label="Olumlu seçenek etiketi"
                    value={olumluEtiket}
                    onChange={(event) => setOlumluEtiket(event.currentTarget.value)}
                    data-testid="sablon-set-soluk-olumlu"
                  />
                  <TextInput
                    placeholder="Olumsuz"
                    aria-label="Olumsuz seçenek etiketi"
                    value={olumsuzEtiket}
                    onChange={(event) => setOlumsuzEtiket(event.currentTarget.value)}
                    data-testid="sablon-set-soluk-olumsuz"
                  />
                </Group>
              </Table.Td>
              <Table.Td />
              <Table.Td />
            </SolukSatir>
          )}
        </Table.Tbody>
      </Table>
      <Text size="xs" c="dimmed" data-testid="sablon-set-konsolidasyon-serhi">
        Kendi yazdığınız seçenekler holding konsolidasyonunda ortak eksen taşımaz; ortak eksen
        gereken sorularda sözlük tabanlı alan kullanın.
      </Text>
    </Stack>
  );
}

/** Bölüm başlığı hücresi — müşteri başlığını satır içi düzenler. */
function BolumBasligi({
  bolum,
  kilitli,
  onBaslik,
}: {
  bolum: FormBolum;
  kilitli: boolean;
  onBaslik: (baslik: string) => void;
}): React.JSX.Element {
  const [taslak, setTaslak] = useState(bolum.baslik ?? '');
  if (kilitli) {
    return (
      <Text size="sm" fw={600}>
        {bolum.baslik ?? 'Genel'}
      </Text>
    );
  }
  const isle = (): void => {
    const yeni = taslak.trim();
    if (!yeni || yeni === bolum.baslik) {
      setTaslak(bolum.baslik ?? '');
      return;
    }
    onBaslik(yeni);
  };
  return (
    <TextInput
      size="xs"
      variant="unstyled"
      fw={600}
      aria-label={`${bolum.baslik ?? 'Genel'} bölüm başlığı`}
      value={taslak}
      onChange={(event) => setTaslak(event.currentTarget.value)}
      onBlur={isle}
      onKeyDown={(event) => {
        if (event.key === 'Enter') isle();
      }}
      data-testid={`sablon-bolum-baslik-${bolum.kod}`}
    />
  );
}

/** Düzenleyicinin bölüm listesi — bildirilmemişse tek ÖRTÜK bölüm. */
function duzenlemeBolumleri(sema: FormSemaTaslak): FormBolum[] {
  const bildirilen = sema.bolumler ?? [];
  if (bildirilen.length === 0) return [{ kod: ORTUK_BOLUM_KODU, baslik: null, sira: 10 }];
  return [...bildirilen].sort((a, b) => a.sira - b.sira || a.kod.localeCompare(b.kod));
}

/** Bir bölümün maddeleri — bölümü çözülemeyen madde İLK bölümde görünür. */
function bolumunAlanlari(
  sema: FormSemaTaslak,
  bolumler: FormBolum[],
  bolumKodu: string,
): FormAlan[] {
  const ilk = bolumler[0]!.kod;
  return [...sema.alanlar]
    .sort((a, b) => a.sira - b.sira || a.kod.localeCompare(b.kod))
    .filter((alan) => {
      const kod = alan.bolumKodu ?? null;
      if (kod === null) return bolumKodu === ilk;
      return bolumler.some((b) => b.kod === kod) ? kod === bolumKodu : bolumKodu === ilk;
    });
}

/** Set seçeneklerinin TEK kaynağı: sürümdeki setler + kütüphane. */
function duzenlemeSetleri(sema: FormSemaTaslak): {
  secenekler: { value: string; label: string }[];
  varsayilanAd: string;
} {
  const harita = new Map<string, string>();
  for (const set of CEVAP_SETI_KUTUPHANESI) harita.set(set.kod, set.ad);
  for (const set of sema.cevapSetleri ?? []) harita.set(set.kod, set.ad);
  const varsayilanKod = sema.varsayilanCevapSetiKodu ?? VARSAYILAN_CEVAP_SETI_KODU;
  return {
    secenekler: [...harita].map(([value, label]) => ({ value, label })),
    varsayilanAd: harita.get(varsayilanKod) ?? varsayilanKod,
  };
}

function benzersizBolumKodu(mevcut: readonly FormBolum[], baslik: string): string {
  const govde = slug(baslik) || 'bolum';
  const kodlar = new Set(mevcut.map((b) => b.kod));
  const taban = `bolum_${govde}`.slice(0, 55);
  if (!kodlar.has(taban)) return taban;
  for (let sayac = 2; ; sayac += 1) {
    const aday = `${taban}_${sayac}`;
    if (!kodlar.has(aday)) return aday;
  }
}

function benzersizSetKodu(mevcut: readonly CevapSeti[], ad: string): string {
  const govde = slug(ad) || 'set';
  const kodlar = new Set(mevcut.map((s) => s.kod));
  const taban = `set_${govde}`.slice(0, 55);
  if (!kodlar.has(taban)) return taban;
  for (let sayac = 2; ; sayac += 1) {
    const aday = `${taban}_${sayac}`;
    if (!kodlar.has(aday)) return aday;
  }
}

/**
 * Etiket hücresi: MÜŞTERİ alanında düzenlenir (odak çıkınca/Enter'da yazılır —
 * her tuşta istek atılmaz), ÜRÜN alanında kilitli metindir.
 */
function AlanEtiketi({
  alan,
  kilitli,
  onEtiket,
}: {
  alan: FormAlan;
  kilitli: boolean;
  onEtiket: (etiket: string) => void;
}): React.JSX.Element {
  const [taslak, setTaslak] = useState(alan.etiket);
  const urun = alan.kaynak === 'urun';
  const isle = (): void => {
    const yeni = taslak.trim();
    if (!yeni || yeni === alan.etiket) {
      setTaslak(alan.etiket);
      return;
    }
    onEtiket(yeni);
  };
  return (
    <Group gap={6} wrap="nowrap">
      {urun || kilitli ? (
        <Text size="sm">{alan.etiket}</Text>
      ) : (
        <TextInput
          size="xs"
          aria-label={`${alan.etiket} başlığı`}
          value={taslak}
          onChange={(event) => setTaslak(event.currentTarget.value)}
          onBlur={isle}
          onKeyDown={(event) => {
            if (event.key === 'Enter') isle();
          }}
          data-testid={`sablon-etiket-${alan.kod}`}
          style={{ flex: 1 }}
        />
      )}
      <Badge size="xs" color={urun ? undefined : ANLAM.bilgi} variant="light">
        {FORM_ALAN_KAYNAK_ETIKET[alan.kaynak] ?? alan.kaynak}
      </Badge>
      {alan.besleme && (
        <Badge size="xs" color={ANLAM.bilgi} variant="light">
          {alan.besleme.toUpperCase()}
        </Badge>
      )}
    </Group>
  );
}

/**
 * Tip hücresi. Değişebilmesi İKİ koşula bağlı: alan MÜŞTERİNİN olacak ve
 * mevcut tipi seçenek kaynağı taşımayan altılıdan gelecek — çoğaltılmış bir
 * seçim alanının tipi (kaynağı `musteri` olsa da) kilitlidir, yoksa seçenek
 * kaynağı öksüz kalır ve sunucu 400 verirdi.
 */
function AlanTipi({
  alan,
  kilitli,
  onTip,
}: {
  alan: FormAlan;
  kilitli: boolean;
  onTip: (tip: FormAlanTipi) => void;
}): React.JSX.Element {
  const secimKaynagi =
    (alan.secenekler ?? null) !== null || (alan.lookupListeKodu ?? null) !== null;
  const degistirilebilir =
    alan.kaynak === 'musteri' && !secimKaynagi && MUSTERI_ALAN_TIPLERI.includes(alan.tip);
  if (!degistirilebilir || kilitli) {
    return (
      <Text size="sm" data-testid={`sablon-tip-${alan.kod}`}>
        {FORM_ALAN_TIPI_ETIKET[alan.tip] ?? alan.tip}
      </Text>
    );
  }
  return (
    <Select
      size="xs"
      aria-label={`${alan.etiket} tipi`}
      data={MUSTERI_ALAN_TIPLERI.map((tip) => ({
        value: tip,
        label: FORM_ALAN_TIPI_ETIKET[tip] ?? tip,
      }))}
      value={alan.tip}
      allowDeselect={false}
      onChange={(value) => value && onTip(value as FormAlanTipi)}
      data-testid={`sablon-tip-${alan.kod}`}
    />
  );
}

/**
 * Alan kodu SABİT KİMLİKTİR (`form_answer.alan_kodu` buna bakar) ve
 * kullanıcıya sorulmaz — başlıktan türetilir. Çakışma sessizce veri
 * kaybettirirdi (aynı kodlu iki alan): mevcut kodlara bakılıp sayaç eklenir.
 */
function benzersizAlanKodu(sema: FormSemaTaslak, etiket: string): string {
  const govde = slug(etiket) || 'alan';
  const mevcut = new Set(sema.alanlar.map((a) => a.kod));
  const taban = `musteri_${govde}`.slice(0, 55);
  if (!mevcut.has(taban)) return taban;
  for (let sayac = 2; ; sayac += 1) {
    const aday = `${taban}_${sayac}`;
    if (!mevcut.has(aday)) return aday;
  }
}

function slug(metin: string): string {
  const harita: Record<string, string> = {
    ç: 'c',
    ğ: 'g',
    ı: 'i',
    ö: 'o',
    ş: 's',
    ü: 'u',
    Ç: 'c',
    Ğ: 'g',
    İ: 'i',
    Ö: 'o',
    Ş: 's',
    Ü: 'u',
  };
  return metin
    .split('')
    .map((harf) => harita[harf] ?? harf)
    .join('')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 40);
}

// ═════════════════════════════════════════════════════════════════════════════
// F4·S6 — KAPSAM SEÇİCİ (Y2; Kapsam §22.5c/T2 · T4)
// ═════════════════════════════════════════════════════════════════════════════

/**
 * KAPSAM = İŞYERİ KÜMESİ. Havuz **SUNUCUDAN** gelir (bahçedeki işyerleri) —
 * ekran ikinci bir bahçe kuralı yazmaz (§2.16/B3).
 *
 * ⚠️ **BOŞ KAPSAM SEÇİLEMEZ:** kapsamsız şablon hiçbir işyerinde geçerli
 * değildir ve listede kimseye görünmez — sessizce ölü doğardı. Kaydet düğmesi
 * pasifleşir ve **SEBEBİ YANINDA YAZAR** (§2.40 / tur 3 artçısı G5: pasif
 * düğmenin sebebi sayfanın altında durmaz).
 *
 * ⚠️ **"Tümünü seç" bir KURAL DEĞİL, bir KOLAYLIKTIR:** seçtiği an kümeyi
 * MATERYALİZE eder (T2). Sonradan açılan işyeri kapsama girmez — bu bilinçli
 * bedeldir ve metinde söylenir.
 */
export function KapsamSecici({
  isyerleri,
  secili,
  onDegisti,
  duzenlenebilir = true,
  testId = 'sablon-kapsam-secici',
}: {
  isyerleri: readonly { id: string; ad: string; companyAd: string }[];
  secili: readonly string[];
  onDegisti: (idler: string[]) => void;
  duzenlenebilir?: boolean;
  testId?: string;
}): React.JSX.Element {
  const tumu = isyerleri.map((w) => w.id);
  const hepsiSecili = tumu.length > 0 && tumu.every((id) => secili.includes(id));
  return (
    <Stack gap={6} data-testid={testId}>
      <Group gap="xs" align="baseline">
        <Text size="xs" c="dimmed">
          Kapsam — bu form hangi işyerlerinde geçerli
        </Text>
        {duzenlenebilir && tumu.length > 0 && (
          <Anchor
            component="button"
            type="button"
            size="xs"
            data-testid={`${testId}-tumu`}
            onClick={() => onDegisti(hepsiSecili ? [] : tumu)}
          >
            {hepsiSecili ? 'Hiçbiri' : 'Tümünü seç'}
          </Anchor>
        )}
      </Group>
      {isyerleri.length === 0 ? (
        <Text size="sm" c="dimmed" data-testid={`${testId}-bos`}>
          Bahçenizde işyeri yok — kapsam seçilemez.
        </Text>
      ) : (
        <Checkbox.Group value={[...secili]} onChange={onDegisti} data-testid={`${testId}-grup`}>
          <Stack gap={4}>
            {isyerleri.map((w) => (
              <Checkbox
                key={w.id}
                value={w.id}
                disabled={!duzenlenebilir}
                label={`${w.companyAd} — ${w.ad}`}
                data-testid={`${testId}-${w.id}`}
              />
            ))}
          </Stack>
        </Checkbox.Group>
      )}
      {duzenlenebilir && (
        <Text size="xs" c="dimmed">
          Sonradan açılan işyerleri kapsama kendiliğinden eklenmez — buradan eklenir.
        </Text>
      )}
    </Stack>
  );
}
